import { useEffect, useState } from "preact/hooks"
import { useFetch } from "../hooks/useFetch"
import type { Topic, User } from "../types"
import { LevelPicker } from "./LevelPicker"
import { LoadError } from "./LoadError"
import {
	acceptFriendRequest,
	addOrInviteFriend,
	createMatch,
	fetchFriends,
	fetchTopics,
	removeFriendship,
} from "./lib/api"
import { Modal } from "./Modal"
import { OnlineDot } from "./OnlineDot"
import { isQuotaError, ProModal } from "./Plan"
import { SiteFooter } from "./SiteFooter"
import { TopicIcon } from "./TopicIcons"
import { TopicList } from "./TopicList"

interface FriendsViewProps {
	navigate: (path: string) => void
}

export function FriendsView({ navigate }: FriendsViewProps) {
	// The list is re-read after every mutation and on a 30s beat — both as
	// background refreshes, so the table never blanks to reload.
	const {
		data,
		error: loadError,
		reload,
		refresh: load,
	} = useFetch(fetchFriends, "Failed to load friends")
	// The challenge modal is nothing but this list, so a swallowed failure here
	// is a modal that spins forever with no way out and no explanation.
	const {
		data: topics,
		error: topicsError,
		reload: loadTopics,
	} = useFetch(fetchTopics, "Couldn't load the topics.")
	// Errors from *actions* (accept, remove, challenge) — the list's own
	// failure is `loadError`, shown beside the list.
	const [error, setError] = useState("")
	// A challenge refused for the free cap (402) closes the topic dialog and
	// opens the Pro one, leading with the server's sentence — the same swap
	// Home makes. Leaving the challenge modal up over a page-level alert hid
	// the reason behind the dialog it was about.
	const [proReason, setProReason] = useState<string | null>(null)
	const [query, setQuery] = useState("")
	const [addError, setAddError] = useState("")
	const [addSuccess, setAddSuccess] = useState("")
	const [adding, setAdding] = useState(false)
	const [challengeTarget, setChallengeTarget] = useState<User | null>(null)
	// The topic chosen inside the challenge modal, if any — step 1 is the
	// topic grid, step 2 (this set) is the level picker and the send button.
	const [challengeTopic, setChallengeTopic] = useState<Topic | null>(null)
	const [level, setLevel] = useState(1)
	const [challenging, setChallenging] = useState(false)

	// Refresh every 30s so the online dots stay honest on a page left open
	// (presence goes stale after two minutes of no requests).
	useEffect(() => {
		const interval = window.setInterval(load, 30000)
		return () => window.clearInterval(interval)
	}, [load])

	function closeChallenge() {
		setChallengeTarget(null)
		setChallengeTopic(null)
	}

	async function handleAddOrInvite(e: Event) {
		e.preventDefault()
		const q = query.trim()
		if (!q) return
		setAddError("")
		setAddSuccess("")
		setAdding(true)
		try {
			const res = await addOrInviteFriend(q)
			setAddSuccess(
				res.kind === "invite"
					? `Invitation sent to ${res.email}.`
					: res.status === "accepted"
						? `You and ${res.user.username} are now friends!`
						: `Friend request sent to ${res.user.username}.`,
			)
			setQuery("")
			load()
		} catch (e) {
			setAddError(e instanceof Error ? e.message : "Failed to add or invite")
		} finally {
			setAdding(false)
		}
	}

	async function handleAccept(id: number) {
		setError("")
		try {
			await acceptFriendRequest(id)
			load()
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to accept request")
		}
	}

	async function handleRemove(id: number) {
		setError("")
		try {
			await removeFriendship(id)
			load()
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to remove")
		}
	}

	async function handleChallenge() {
		if (!challengeTarget || !challengeTopic) return
		setChallenging(true)
		setError("")
		try {
			const state = await createMatch(
				challengeTopic.id,
				level,
				challengeTarget.id,
			)
			navigate(`/match/${state.id}`)
		} catch (e) {
			if (isQuotaError(e)) {
				closeChallenge()
				setProReason(e.message)
			} else {
				setError(e instanceof Error ? e.message : "Failed to send challenge")
			}
			setChallenging(false)
		}
	}

	const people = data?.people ?? []

	return (
		<div class="overflow-auto h-100">
			<div class="container page">
				<h1>Friends</h1>
				{error && <div class="alert danger inline">{error}</div>}

				<div class="stack gap-2">
					<h2>Add/invite a friend</h2>
					<p class="muted small">
						Enter a username to add an existing member, or an email — they'll
						get an invite if they're new, or a friend request if they're already
						a member.
					</p>
					<form onSubmit={handleAddOrInvite} class="flex wrap gap-2">
						<div class="grow">
							<input
								type="text"
								placeholder="username or email"
								value={query}
								onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
							/>
						</div>
						<button type="submit" class="primary nowrap" disabled={adding}>
							Add or invite
						</button>
					</form>
					{addSuccess && <p class="text-success small">{addSuccess}</p>}
					{addError && <p class="text-danger small">{addError}</p>}
				</div>

				<div class="stack gap-2">
					{/* "Nothing here yet" is a claim about the account, made only from
					    a list that arrived. A failed fetch says so instead; a failed
					    *refresh* says so above the list it could not replace. */}
					{loadError && <LoadError message={loadError} onRetry={reload} />}
					{!data ? null : people.length === 0 ? (
						<p class="muted small">
							Nothing here yet — add or invite someone above.
						</p>
					) : (
						<div class="table-wrap">
							<table>
								<thead>
									<tr>
										<th scope="col">Name</th>
										<th scope="col">Status</th>
										<th scope="col" class="text-end">
											Actions
										</th>
									</tr>
								</thead>
								<tbody>
									{people.map((p) => (
										<tr key={`${p.status}-${p.id}`}>
											<td>
												{p.user ? (
													// Dot trails the name: an absent dot would
													// otherwise indent offline rows differently.
													<span class="inline-flex center gap-2">
														<a href={`/u/${p.user.username}`} class="plain">
															{p.user.username}
														</a>
														<OnlineDot online={p.online} />
													</span>
												) : (
													// An invited person has no account yet, so the
													// address the inviter typed is the only name we
													// have for them. There is no separate Email
													// column: a member's address is never sent to
													// anyone else, so it would be blank on every
													// other row.
													<span class="muted">{p.email}</span>
												)}
											</td>
											<td>
												{p.status === "incoming" ? (
													<button
														type="button"
														class="success sm"
														onClick={() => handleAccept(p.id)}
													>
														Accept
													</button>
												) : p.status === "friend" ? null : (
													<span
														class={
															p.status === "rejected"
																? "text-danger small"
																: "muted small"
														}
													>
														{p.status === "rejected" ? "Rejected" : "Pending"}
													</span>
												)}
											</td>
											<td class="text-end">
												{p.status === "friend" && p.user && (
													<button
														type="button"
														class="outline sm"
														onClick={() => {
															setChallengeTarget(p.user)
															setChallengeTopic(null)
														}}
													>
														Challenge
													</button>
												)}
												{p.status === "incoming" && (
													<button
														type="button"
														class="link danger sm"
														onClick={() => handleRemove(p.id)}
													>
														Decline
													</button>
												)}
												{p.status === "outgoing" && (
													<button
														type="button"
														class="link danger sm"
														onClick={() => handleRemove(p.id)}
													>
														Cancel
													</button>
												)}
												{p.status === "rejected" && (
													<button
														type="button"
														class="link danger sm"
														onClick={() => handleRemove(p.id)}
													>
														Dismiss
													</button>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>

				<SiteFooter />
			</div>

			<Modal
				open={challengeTarget !== null}
				onClose={closeChallenge}
				title={
					challengeTopic ? (
						<span class="flex center gap-2">
							<TopicIcon topic={challengeTopic.id} size="1.25rem" />
							{challengeTopic.name}
						</span>
					) : challengeTarget ? (
						`Challenge ${challengeTarget.username}`
					) : (
						""
					)
				}
			>
				{topicsError ? (
					<LoadError message={topicsError} onRetry={loadTopics} />
				) : !topics ? (
					<div class="spinner sm" role="status" />
				) : challengeTopic ? (
					<div class="stack gap-3">
						<p>{challengeTopic.description}</p>
						<LevelPicker
							levels={challengeTopic.levels}
							level={level}
							onChange={setLevel}
						/>
						<button
							type="button"
							autofocus
							class="primary"
							disabled={challenging}
							onClick={handleChallenge}
						>
							Send challenge
						</button>
						<button
							type="button"
							class="link muted self-start"
							disabled={challenging}
							onClick={() => setChallengeTopic(null)}
						>
							Back
						</button>
					</div>
				) : (
					<TopicList
						topics={topics}
						onSelect={(t) => {
							setChallengeTopic(t)
							setLevel(1)
						}}
					/>
				)}
			</Modal>
			<ProModal
				open={proReason !== null}
				onClose={() => setProReason(null)}
				reason={proReason || undefined}
			/>
		</div>
	)
}
